// Build a strike-indexed option chain { strike -> { call, put } }
export function buildChain(rowsBySym) {
  const map = new Map();
  let S = null;
  for (const sym in rowsBySym) {
    const r = rowsBySym[sym];
    if (r.S != null) S = r.S;
    const k = Number(r.strike);
    const bucket = map.get(k) || { call: null, put: null };
    if (r.optionType === 'C') bucket.call = r;
    else if (r.optionType === 'P') bucket.put = r;
    map.set(k, bucket);
  }
  const strikes = Array.from(map.keys()).sort((a, b) => a - b);
  return { S, strikes, map };
}

// Treat ATM as OTM per your requirement
export function isITM(row) {
  if (!row) return false;
  return Number(row.distance) > 0; // schema: >0 means ITM for that type
}

// Gross delta flow for one option row — the denominator for "lean" gauges.
// Prefer |bought| + |sold| so two-sided flow grades the lean: net/|net| alone
// saturates at 100% whenever every side happens to point the same way (e.g. at
// the open: calls net-bought AND puts net-sold are both positive net delta).
// Falls back to |net| when the feed doesn't carry the buy/sell components.
export function deltaFlowGross(row) {
  if (!row) return 0;
  const buy = Number(row.cumulativeDeltaBuy);
  const sell = Number(row.cumulativeDeltaSell);
  const gross = (Number.isFinite(buy) ? Math.abs(buy) : 0)
              + (Number.isFinite(sell) ? Math.abs(sell) : 0);
  if (gross > 0) return gross;
  const net = Number(row.cumulativeDeltaNet);
  return Number.isFinite(net) ? Math.abs(net) : 0;
}

// The chain's focus strike: first strike (scanning low -> high) where the call
// side flips ITM -> OTM. Derived purely from the rows' own `distance` field, so
// it stays current whenever the chain updates - unlike the agg-feed spot, which
// can go stale outside market hours. Both chain tables mark this row with the
// gold divider; sharing it here keeps their focus identical.
export function atmBoundaryStrike(chain) {
  if (!Array.isArray(chain?.strikes) || chain.strikes.length === 0) return null;
  let prevCallITM, havePrev = false;
  for (const k of chain.strikes) {
    const callRow = chain.map.get(k)?.call;
    if (!callRow) continue;
    const cur = isITM(callRow);
    if (havePrev && prevCallITM === true && cur === false) return k;
    prevCallITM = cur;
    havePrev = true;
  }
  return null;
}
